import React from "react";
import { Box } from "@mui/material";
import { createColumnHelper } from "@tanstack/react-table";

// Components
import CheckboxColumn from "components/Table/CheckboxColumn";
import EllipsisTooltipCell from "utils/ellipsisTooltipCell";

// Global Utils
import getSwitchPortStatus, { getSwitchPortStatusLabel } from "utils/getSwitchPortStatus";
import { formatTrafficBytes } from "utils/format";

// Helper
import {
  getPortTypeOptionsLabel
} from '../helper';

// Local Const
import {
  linkSpeedOptions,
  portStaSpeedMap,
  accessPolicyTypeDefinition,
  operationDefinition,
  defaultPortTypes
} from "../../const";

const columnHelper = createColumnHelper();

const getSwitchesColumns = (
  showCheckbox = true,
  scheduleOptions = [],
  portTagsMap
) => {
  const baseColumns = [
    columnHelper.accessor("deviceName", {
      header: "Device Name",
      cell: (info) => <Box>{info.getValue()}</Box>,
    }),
    columnHelper.accessor("portIndex", {
      header: "Port",
      cell: (info) => <Box>{info.getValue()}</Box>,
    }),
    columnHelper.accessor("stateLabel", {
      id: "state",
      header: "State",
      cell: (info) => {
        if (!info.column) {
          return <Box>{info.getValue()}</Box>;
        }
        const value = info.row.original.state;

        if (!value || value === "-") {
          return <Box>-</Box>;
        }

        if (
          React.isValidElement(value) &&
          value.type === "img" &&
          !value.props.src
        ) {
          return <Box>-</Box>;
        }

        return <Box>{value}</Box>;
      },
    }),
    columnHelper.accessor("portName", {
      header: "Port Name",
      cell: (info) => <Box>{info.getValue() ?? "-"}</Box>,
    }),
    columnHelper.accessor("portProfileName", {
      header: "Port Profile",
      cell: (info) => {
        const value = info.getValue();
        const isProfileValid = value != null && value !== "No Profile";

        return <EllipsisTooltipCell text={isProfileValid ? value : "-"} />
      },
    }),
    columnHelper.accessor("speedLabel", {
      header: "Port Speed",
      cell: (info) => {
        const row = info.row.original;
        const selectedProfile = row.selectedProfile;
        const switchPort = row.switchPort;
        const fallbackSpeed = row.speed;

        let label = "-";

        if (selectedProfile && !selectedProfile.isDefault) {
          const matchedProfileSpeed = linkSpeedOptions.find(speed => speed.value === selectedProfile?.speed);
          label = matchedProfileSpeed?.label ?? "-";
        } else {
          if (!switchPort) {
            const matchedSpeed = linkSpeedOptions.find(opt => opt.value === fallbackSpeed);
            label = matchedSpeed ? matchedSpeed.label : "-";
          } else if (switchPort.portStaStatus === 2) {
            label = "-";
          } else {
            label = portStaSpeedMap[switchPort.portStaSpeed] || "-";
          }
        }

        return <Box>{label}</Box>;
      },
    }),

    columnHelper.accessor((row) => {
      return getPortTypeOptionsLabel(row.portType)
    }, {
      id: "portTypeLabel",
      header: "Port Type",
      cell: (info) => <Box>{info.getValue()}</Box>,
    }),
    columnHelper.accessor((row) => {
      const opType = row?.operation?.type;
      const groupId = row?.operation?.aggregateGroupId;
      return opType === "aggregation" ? `Aggregate #${groupId}` : operationDefinition[opType]?.label;
    }, {
      id: "operationLabel",
      header: "Operation",
      cell: (info) => <Box>{info.getValue() || "-"}</Box>,
    }),
    columnHelper.accessor("PVID", {
      header: "PVID",
      cell: (info) => <Box>{info.getValue() ?? "-"}</Box>,
    }),
    columnHelper.accessor((row) => {
      const portType = row.portType;
      const allowVlan = row.allowVlan;
      if (portType === defaultPortTypes.access.value) return "-";
      if (Array.isArray(allowVlan)) {
        if (allowVlan.length === 0) return "-";
        else return allowVlan.join(", ");
      } else {
        return allowVlan ?? "-";
      }

    }, {
      id: "allowVlan",
      header: "Allowed VLANS",
      cell: (info) => <Box>{info.getValue()}</Box>,
    }),
    columnHelper.accessor((row) => {
      const value = row.poeState;
      const isPoeScheduleMode = row.isPoeScheduleMode;

      if (isPoeScheduleMode) return "Schedule";
      else return value ? "Enabled" : "Disabled";
    }, {
      id: "poeState",
      header: "PoE",
      cell: (info) => <Box>{info.getValue()}</Box>,
    }),
    columnHelper.accessor("poePowerUsage", {
      header: "PoE Power Usage (W)",
      cell: (info) => {
        const poe = info.row.original.poe;
        if (poe !== true || (poe && parseInt(info.getValue(), 10) < 0)) {
          return <Box>-</Box>;
        }

        return <Box>{info.getValue() || "-"}</Box>;
      },
    }),
    columnHelper.accessor("modelName", {
      header: "Model Name",
      cell: (info) => <Box>{info.getValue() || "-"}</Box>,
    }),
    columnHelper.accessor((row) => {
      return row.rstpState ? "Enabled" : "Disabled";
    }, {
      id: "rstp",
      header: "RSTP",
      cell: (info) => <Box>{info.getValue() ?? "-"}</Box>,
    }),
    columnHelper.accessor((row) => {
      const value = row.lbdState;
      return !!value ? "Enabled" : "Disabled";
    }, {
      id: "lbdState",
      header: "LBD",
      cell: (info) => <Box>{info.getValue()}</Box>,
    }),
    columnHelper.accessor((row) => {
      return row.ddpState === true ? "Enabled" : "Disabled";
    }, {
      id: "ddpState",
      header: "DDP",
      cell: (info) => <Box>{info.getValue()}</Box>,
    }),
    columnHelper.accessor((row) => {
      return row.lldp === true ? "Enabled" : "Disabled";
    }, {
      id: "lldp",
      header: "LLDP",
      cell: (info) => <Box>{info.getValue()}</Box>,
    }),
    columnHelper.accessor((row) => {
      return row.stpState ? "Enabled" : "Disabled";
    }, {
      id: "stpGuard",
      header: "STP Guard",
      cell: (info) => <Box>{info.getValue()}</Box>,
    }),
    columnHelper.accessor((row) => {
      const value = row.portSchedule;
      const matched = scheduleOptions.find(opt => opt.value === value);
      return matched ? matched.label : (value || "-");
    }, {
      id: "portSchedule",
      header: "Port Schedule",
      cell: (info) => <Box>{info.getValue()}</Box>,
    }),
    columnHelper.accessor((row) => {
      const value = row.poeSchedule;
      const matched = scheduleOptions.find(opt => opt.value === value);
      return matched ? matched.label : (value || "-");
    }, {
      id: "poeSchedule",
      header: "PoE Schedule",
      cell: (info) => <Box>{info.getValue()}</Box>,
    }),
    columnHelper.accessor((row) => {
      const accessPolicy = row.accessPolicy;
      if (!accessPolicy?.enableState) return accessPolicyTypeDefinition.DISABLED.label;
      return accessPolicyTypeDefinition[accessPolicy.type]?.label ?? accessPolicyTypeDefinition.DISABLED.label;
    }, {
      id: "accessPolicyLabel",
      header: "Access Policies",
      cell: (info) => <Box>{info.getValue() ?? "-"}</Box>,
    }),
    columnHelper.accessor((row) => {
      const tagNames = row.portTags.map(tagId => portTagsMap[tagId] ?? null).filter(Boolean);
      return tagNames.length > 0 ? tagNames.join(", ") : "-";
    }, {
      id: "tagsLabel",
      header: "Tags",
      cell: (info) => <EllipsisTooltipCell text={info.getValue()} />,
    }),
    columnHelper.accessor("deviceIp", {
      header: "Device Ip",
      cell: (info) => <Box>{info.getValue() || "-"}</Box>,
    }),
    columnHelper.accessor("txTraffic", {
      header: "Tx Traffic",
      cell: (info) => <Box>{info.getValue() ? formatTrafficBytes(info.getValue()) : "-"}</Box>,
    }),
    columnHelper.accessor("rxTraffic", {
      header: "Rx Traffic",
      cell: (info) => <Box>{info.getValue() ? formatTrafficBytes(info.getValue()) : "-"}</Box>,
    }),
    columnHelper.accessor("txMulticast", {
      header: "Tx Multicast",
      cell: (info) => <Box>{info.getValue() || "-"}</Box>,
    }),
    columnHelper.accessor("rxMulticast", {
      header: "Rx Multicast",
      cell: (info) => <Box>{info.getValue() || "-"}</Box>,
    }),
    columnHelper.accessor("txBroadcast", {
      header: "Tx Broadcast",
      cell: (info) => <Box>{info.getValue() || "-"}</Box>,
    }),
    columnHelper.accessor("rxBroadcast", {
      header: "Rx Broadcast",
      cell: (info) => <Box>{info.getValue() || "-"}</Box>,
    }),
    columnHelper.accessor("txPackets", {
      header: "Tx Packets",
      cell: (info) => <Box>{info.getValue() || "-"}</Box>,
    }),
    columnHelper.accessor("rxPackets", {
      header: "Rx Packets",
      cell: (info) => <Box>{info.getValue() || "-"}</Box>,
    }),
    columnHelper.accessor("totalTraffic", {
      header: "Total Traffic",
      cell: (info) => <Box>{info.getValue() ? formatTrafficBytes(info.getValue()) : "-"}</Box>,
    }),
  ];

  return showCheckbox
    ? [CheckboxColumn(columnHelper), ...baseColumns]
    : baseColumns;
};

export default getSwitchesColumns;
